import { parseCsvGrid } from './types';
import type { ProspectInput, ProspectStatus } from './prospects';

/**
 * Reading Senku's contacts sheet, whether it arrives by paste or by import.
 *
 * Until drop 5 the day's prospects arrive by Hanneza selecting rows in the
 * "4ocean Leads Contacts" sheet and pasting; after it, the same rows are read
 * out of Drive by an edge function. Both go through here, so the two can
 * never disagree about what a row means.
 *
 * Three things about that sheet decide this parser:
 *
 * 1. Google Sheets copies cells as TAB-separated text, not CSV. A header row
 *    containing a tab is read as TSV; anything else goes through the CSV grid
 *    the lead import already uses.
 * 2. The sheet has one Name column, not First/Last. It is split on the last
 *    space, which is right for "Ana Lucia Ortiz" more often than the first
 *    space is, and either half can be corrected in the drawer afterwards.
 * 3. Empty cells are not empty. See below — this one nearly cost an import.
 *
 * Her Status column is carried across as-is (Pending / Connected / Left
 * Company / Can't send connection request) so a prospect she already worked
 * does not arrive as Pending. No activity is logged for an imported status:
 * the work predates the Hub and its date is unknown — the same rule the touch
 * backfill followed. Invent nothing.
 */

// ── shared rules: keep identical with supabase/functions/import-prospects ───

type Field =
  | 'name'
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'title'
  | 'company_name'
  | 'linkedin_url'
  | 'why_relevant'
  | 'opening_hook'
  | 'status'
  | 'sourced_on';

const HEADER_ALIASES: Record<string, Field> = {
  name: 'name',
  'full name': 'name',
  contact: 'name',
  'first name': 'first_name',
  firstname: 'first_name',
  first: 'first_name',
  'last name': 'last_name',
  lastname: 'last_name',
  last: 'last_name',
  email: 'email',
  'email address': 'email',
  phone: 'phone',
  'phone number': 'phone',
  title: 'title',
  'job title': 'title',
  role: 'title',
  company: 'company_name',
  'company name': 'company_name',
  organization: 'company_name',
  'linkedin url': 'linkedin_url',
  linkedin: 'linkedin_url',
  'linkedin profile': 'linkedin_url',
  'profile url': 'linkedin_url',
  // LinkedIn's own connections export calls them URL and Position.
  url: 'linkedin_url',
  position: 'title',
  'why relevant': 'why_relevant',
  why: 'why_relevant',
  relevance: 'why_relevant',
  'opening hook': 'opening_hook',
  hook: 'opening_hook',
  status: 'status',
  // The day the research found them, which is not the day the Hub heard of
  // them. See 20270123000100.
  date: 'sourced_on',
  'date found': 'sourced_on',
  sourced: 'sourced_on',
  // The connections export's own column. It is the day they ACCEPTED, which
  // is a different fact from the day research found somebody — see
  // importSourceDateMeaning below for why one column still holds both.
  'connected on': 'sourced_on',
};

/**
 * Where a paste came from. Chosen in the Add prospects drawer, because the
 * same three columns mean different things depending on the answer.
 */
export type ImportSource = 'senku_daily' | 'linkedin_connections' | 'old_tracker';

export const IMPORT_SOURCES: { key: ImportSource; label: string; hint: string }[] = [
  { key: 'senku_daily', label: "Senku's daily sheet", hint: 'Date is the day research found them' },
  {
    key: 'linkedin_connections',
    label: 'LinkedIn connections',
    hint: 'Date is the day they accepted',
  },
  { key: 'old_tracker', label: 'Old tracker', hint: 'Date is whatever the export carries' },
];

/**
 * One date column, two meanings, and why that is not a fudge.
 *
 * sourced_on is "the day this person entered our world", and for Senku's
 * sheet that is the day research found them while for a connections export it
 * is the day they accepted. Those are different events, but they play the
 * same part: they are the date the batch is named from and the date the list
 * sorts by. A second column would be honest about the difference and useless
 * about everything else — every reader would have to coalesce the two, and
 * the first one that forgot would sort 364 people to the bottom.
 *
 * Recorded here so nobody later reads a March date on a connections row as
 * "we researched them in March".
 */
export function importSourceDateMeaning(source: ImportSource): string {
  return IMPORT_SOURCES.find((entry) => entry.key === source)?.hint ?? '';
}

/**
 * Hanneza's spellings in the sheet, plus ours. Anything else is New.
 *
 * The fallback changed in 20270125000100. It used to be Pending, which made
 * every freshly researched contact look like a connection request had already
 * gone out. A blank status cell means nobody has touched the person yet, and
 * that is what New says.
 */
const STATUS_ALIASES: Record<string, ProspectStatus> = {
  new: 'new',
  'not connected': 'new',
  pending: 'pending',
  connected: 'connected',
  pitched: 'pitched',
  '1st dm': 'pitched',
  'first dm': 'pitched',
  '2nd dm': 'dm_2',
  'second dm': 'dm_2',
  dm_2: 'dm_2',
  '3rd dm': 'dm_3',
  'third dm': 'dm_3',
  dm_3: 'dm_3',
  'left company': 'left_company',
  left_company: 'left_company',
  left: 'left_company',
  "can't connect": 'cant_connect',
  'cant connect': 'cant_connect',
  cant_connect: 'cant_connect',
  "can't send connection request": 'cant_connect',
  'cant send connection request': 'cant_connect',
};

/**
 * A blank Status cell does not mean the same thing in every sheet.
 *
 * In Senku's it means nobody has touched the person yet, which is New —
 * 20270125000100 changed the fallback from Pending for exactly that reason.
 * In a LinkedIn connections export, every row in the file is somebody who
 * ALREADY ACCEPTED; that is what the file is. Reading those 364 as New would
 * say nobody had done anything, when in fact a request was sent and answered.
 *
 * A status the sheet states always wins, whichever file it came from.
 */
export function importedStatus(raw: string, source: ImportSource = 'senku_daily'): ProspectStatus {
  const stated = STATUS_ALIASES[raw.trim().toLowerCase().replace(/[’‘]/g, "'")];
  if (stated !== undefined) return stated;
  return source === 'linkedin_connections' ? 'connected' : 'new';
}

/**
 * A cell that says the research came up empty is EMPTY, not a value.
 *
 * Measured in the live CG sheet on 9 September, 94 contacts: 11 rows carry
 * `not found` in LinkedIn URL and 86 carry `not published` in Email. Senku
 * writes those deliberately — "we looked and there wasn't one" is worth more
 * to a reader than a blank.
 *
 * To the database they are not blanks, they are ONE value repeated. The
 * unique indexes are `lower(btrim(linkedin_url))` and `lower(btrim(email))`
 * with a `<> ''` exemption, so 86 rows all claiming `not published` collide:
 * the first inserts and the other 85 are rejected as duplicates. The first
 * import would have landed 9 of 94 contacts and reported the rest as already
 * in the Hub — the whole feature quietly doing almost nothing.
 *
 * So a LinkedIn URL that is not a URL, and an email with no `@`, become ''.
 * They then fall under the indexes' blank exemption and stop colliding, and
 * nobody reads "not published" on screen where an address should be.
 */
export function sanitizeLinkedIn(raw: string): string {
  const value = raw.trim();
  return /^https?:\/\//i.test(value) ? value : '';
}

export function sanitizeEmail(raw: string): string {
  const value = raw.trim();
  return value.includes('@') ? value : '';
}

const MONTHS: Record<string, string> = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

/**
 * A day, or null.
 *
 * ISO passes through. LinkedIn's export writes "17 Mar 2026", which the ISO
 * test alone reads as no date at all — and a batch named from no date is
 * every one of the 364 landing in one unnamed pile, which is the whole
 * feature failing quietly. Anything else is still null on purpose: a sheet
 * that writes "September 8" is better read as no date than as a date nobody
 * can order by.
 */
export function parseImportDay(raw: string): string | null {
  const value = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const named = /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/.exec(value);
  if (named === null) return null;
  const month = MONTHS[(named[2] ?? '').slice(0, 3).toLowerCase()];
  if (month === undefined) return null;
  const day = (named[1] ?? '').padStart(2, '0');
  return `${named[3] ?? ''}-${month}-${day}`;
}

/** "Ana Lucia Ortiz" → Ana Lucia / Ortiz. One word is a first name. */
export function splitName(full: string): { first: string; last: string } {
  const trimmed = full.trim().replace(/\s+/g, ' ');
  const at = trimmed.lastIndexOf(' ');
  if (at === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, at), last: trimmed.slice(at + 1) };
}

function parseGrid(text: string): string[][] {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  if (!firstLine.includes('\t')) return parseCsvGrid(text);
  return text
    .split(/\r?\n/)
    .map((line) => line.split('\t').map((cell) => cell.trim()))
    .filter((cells) => cells.some((value) => value !== ''));
}

export function normaliseLinkedIn(url: string): string {
  return url.trim().toLowerCase().replace(/\/+$/, '');
}

/** Same person, when neither an address nor an email says so. */
function nameKey(first: string, last: string, company: string): string {
  return `${first} ${last}|${company}`.trim().toLowerCase().replace(/\s+/g, ' ');
}

function mappingFor(cells: readonly string[]): (Field | null)[] {
  return cells.map((cell) => HEADER_ALIASES[cell.trim().toLowerCase()] ?? null);
}

/**
 * Which row is the header.
 *
 * Not assumed to be the first one. A sheet that gains a title row above its
 * headings — or an export that renders one — would otherwise map nothing and
 * refuse the whole file, and the fix would have to be shipped before the next
 * morning's run. Scanning a few rows for the one that maps at least two known
 * columns costs nothing and cannot be wrong in a way that matters: a data row
 * does not have two column names in it.
 */
function findHeader(grid: readonly string[][]): { at: number; mapping: (Field | null)[] } | null {
  const limit = Math.min(grid.length, 8);
  for (let row = 0; row < limit; row += 1) {
    const mapping = mappingFor(grid[row] ?? []);
    const known = mapping.filter((field) => field !== null).length;
    const identifies =
      mapping.includes('name') ||
      mapping.includes('first_name') ||
      mapping.includes('linkedin_url');
    if (known >= 2 && identifies) return { at: row, mapping };
  }
  return null;
}

export interface ProspectImportResult {
  rows: ProspectInput[];
  /** Already in the Hub (by LinkedIn URL, email, or name and company), or repeated inside the paste. */
  duplicates: number;
  /** Rows with nobody on them. */
  skipped: number;
  error: string;
}

/**
 * What the parser needs to know about a prospect the Hub already holds.
 *
 * Structural rather than `Pick<Prospect, …>`: this block is mirrored into an
 * Edge Function that has no Prospect type, and a shape both sides can state
 * identically is worth more than the tie to the generated one. A Prospect
 * satisfies it, which is what the call sites pass.
 */
export interface KnownProspect {
  linkedin_url: string;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
}

export function parseProspectImport(
  text: string,
  existing: readonly KnownProspect[],
  source: ImportSource = 'senku_daily',
): ProspectImportResult {
  const grid = parseGrid(text);
  if (grid.length < 2) {
    return {
      rows: [],
      duplicates: 0,
      skipped: 0,
      error: 'Paste a header row and at least one prospect.',
    };
  }
  const header = findHeader(grid);
  if (header === null) {
    return {
      rows: [],
      duplicates: 0,
      skipped: 0,
      error: 'No recognizable columns. Include at least Name or LinkedIn URL.',
    };
  }

  // The same three keys the loop below uses, seeded with what the Hub holds.
  // A prospect with no address and no email has no identity the database can
  // enforce, so name and company is what stops the same person arriving again
  // out of a rolling sheet tomorrow.
  const knownUrls = new Set(
    existing
      .map((p) => normaliseLinkedIn(sanitizeLinkedIn(p.linkedin_url)))
      .filter((u) => u !== ''),
  );
  const knownEmails = new Set(
    existing.map((p) => sanitizeEmail(p.email).toLowerCase()).filter((e) => e !== ''),
  );
  const knownNames = new Set(
    existing
      .map((p) => nameKey(p.first_name, p.last_name, p.company_name))
      .filter((n) => n !== '|'),
  );

  const rows: ProspectInput[] = [];
  let duplicates = 0;
  let skipped = 0;

  for (const cells of grid.slice(header.at + 1)) {
    const raw: Record<Field, string> = {
      name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      title: '',
      company_name: '',
      linkedin_url: '',
      why_relevant: '',
      opening_hook: '',
      status: '',
      sourced_on: '',
    };
    header.mapping.forEach((field, index) => {
      if (field !== null) raw[field] = (cells[index] ?? '').trim();
    });
    if (raw.first_name === '' && raw.last_name === '' && raw.name !== '') {
      const split = splitName(raw.name);
      raw.first_name = split.first;
      raw.last_name = split.last;
    }
    const linkedin = sanitizeLinkedIn(raw.linkedin_url);
    const email = sanitizeEmail(raw.email);
    if (raw.first_name === '' && raw.last_name === '' && email === '' && linkedin === '') {
      skipped += 1;
      continue;
    }
    const urlKey = normaliseLinkedIn(linkedin);
    const emailKey = email.toLowerCase();
    const personKey = nameKey(raw.first_name, raw.last_name, raw.company_name);
    if (
      (urlKey !== '' && knownUrls.has(urlKey)) ||
      (emailKey !== '' && knownEmails.has(emailKey)) ||
      (urlKey === '' && emailKey === '' && knownNames.has(personKey))
    ) {
      duplicates += 1;
      continue;
    }
    if (urlKey !== '') knownUrls.add(urlKey);
    if (emailKey !== '') knownEmails.add(emailKey);
    knownNames.add(personKey);
    rows.push({
      first_name: raw.first_name,
      last_name: raw.last_name,
      email,
      phone: raw.phone,
      title: raw.title,
      company_name: raw.company_name,
      linkedin_url: linkedin,
      why_relevant: raw.why_relevant,
      opening_hook: raw.opening_hook,
      status: importedStatus(raw.status, source),
      source,
      sourced_on: parseImportDay(raw.sourced_on),
    });
  }
  return { rows, duplicates, skipped, error: '' };
}
// ── end shared rules ────────────────────────────────────────────────────────
